import type { CallStatusResourceResponseApi } from '@ingenico/api-sdk-device-management-private';
import type { SearchDeviceCallsV2MutationRequestApi } from '@ingenico/api-sdk-device-management-private-v2';
import type { SearchOrganizationsV1QueryParamsApi } from '@ingenico/api-sdk-provisioning-public';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import {
  useBusinessServiceRequest,
  useCurrentUser,
  useCurrentUserDateTimeFormat,
  useQueryFirstLoading,
} from '@ingenico/react-section-utils';
import { useQuickFilters } from '@ingenico/react-ui/advanced-search';
import { DATAGRID_DEFAULT_PAGE_SIZE } from '@ingenico/react-ui/datagrid';
import type { OnTriggerProps, UseDatagridQueryTriggerProps } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDatagridQueryTrigger } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDateRange } from '@ingenico/react-ui/hooks/use-date-range';
import { PeriodSelection } from '@ingenico/react-ui/models';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import type { GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { toString } from 'lodash-es';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useLogicalDeviceHistoryTabColumns } from '../../hooks/useLogicalDeviceHistoryTabColumns.hook';
import { useGridPropsSetup } from './hooks/useGriPropsSetup.hook';
import { useInitialSetup } from './hooks/useInitialSetup.hook';
import { useQuickFiltersSetup } from './hooks/useQuickFiltersSetup.hook';
import { useSearchQuerySetup } from './hooks/useSearchQuerySetup.hook';
import type { UseLogicalDeviceHistoryTab } from './logicalDeviceHistoryTab.model';
import {
  defaultLogicalDeviceHistorySort,
  getLogicalDeviceHistoryTabErrorMsg,
  transformFiltersToRequestCall,
  transformSortModelForRequest,
} from './LogicalDeviceHistoryTab.util';

export function useLogicalDeviceHistoryTab(): UseLogicalDeviceHistoryTab {
  const { t, datagridRef, navigate, mapperOfStatus, isTranslationReady } = useInitialSetup();

  const [offset, setOffset] = useState(0);

  const [limit, setLimit] = useState(DATAGRID_DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<GridSortModel>(defaultLogicalDeviceHistorySort);

  const { logicalDeviceId } = useParams<{
    logicalDeviceId: string;
  }>();

  const { locale: userLocale, zoneInfo } = useCurrentUser();

  const {
    startDate,
    endDate,
    getDateRangeStatus,
    setDateRange,
    isError: isDateRangeError,
    setPeriod,
    timePeriod,
    isStartDateError,
    isEndDateError,
  } = useDateRange({ customPeriod: PeriodSelection.Last30Days, zoneInfo });

  const { columns } = useLogicalDeviceHistoryTabColumns();

  const { searchQuery, selectedQuickFilterValue, setDebouncedSearchQuery, setSelectedQuickFilterValue } =
    useSearchQuerySetup(setOffset);

  const {
    getQuickFilters,
    getLabelQuickFilterLabel,
    quickFilterCall,
    checkAllowReset,
    overrideDefaultFilterTypes,
    handleFilterChange,
  } = useQuickFiltersSetup(t, mapperOfStatus, setSelectedQuickFilterValue, setOffset);

  const body = useMemo<SearchDeviceCallsV2MutationRequestApi>(
    () => ({
      filters: transformFiltersToRequestCall({ startDate, endDate, selectedQuickFilterValue, searchQuery }),
      sort: transformSortModelForRequest(sort),
    }),
    [startDate, endDate, selectedQuickFilterValue, searchQuery, sort],
  );

  const params = useMemo<SearchOrganizationsV1QueryParamsApi>(() => {
    return { offset, limit };
  }, [offset, limit]);

  const query = useMemo(() => ({ body, params, deviceId: toString(logicalDeviceId) }), [body, logicalDeviceId, params]);

  const deviceCallLogsQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback((deviceManagementService) => deviceManagementService.getSearchDeviceCallStatusFront(query), [query]),
  );

  const total = useMemo<number>(() => {
    return deviceCallLogsQuery.data?.pagination?.totalResults ?? 0;
  }, [deviceCallLogsQuery.data?.pagination?.totalResults]);

  const onTrigger = useCallback(({ page, pageSize, sort: newSort }: OnTriggerProps) => {
    setOffset(page * pageSize);
    setLimit(pageSize);
    setSort(newSort);
  }, []);

  const serverData = useMemo<UseDatagridQueryTriggerProps['serverData']>(() => {
    return {
      offset,
      limit,
      total,
    };
  }, [limit, offset, total]);

  const datagridTriggers = useDatagridQueryTrigger({
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    onTrigger,
    serverData,
    filters: { searchQuery, selectedQuickFilterValue },
  });

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<CallStatusResourceResponseApi>) => {
      const { callId } = row;
      void navigate(`calls/${callId}`);
    },
    [navigate],
  );

  const { filterRefApi, isLoadingQuickFilters, fields } = useQuickFilters({
    defaultQuickFilters: getQuickFilters(),
    getLabel: getLabelQuickFilterLabel,
  });

  const getRowId = useCallback((element: CallStatusResourceResponseApi) => toString(element.callId), []);

  const isLoading = useMemo(() => deviceCallLogsQuery.isLoading, [deviceCallLogsQuery.isLoading]);

  const isFirstLoading = useQueryFirstLoading(deviceCallLogsQuery) && !deviceCallLogsQuery?.isError;

  const shouldDisplayGrid = useMemo(
    () => (deviceCallLogsQuery.isSuccess || isLoading) && isTranslationReady,
    [deviceCallLogsQuery.isSuccess, isLoading, isTranslationReady],
  );

  const gridProps = useGridPropsSetup({
    handleRowClick,
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    total,
    getRowId,
    isLoading,
    shouldDisplayGrid,
    onPaginationModelChange: datagridTriggers.onPaginationModelChange,
    onSortModelChange: datagridTriggers.onSortModelChange,
  });

  const filteredDeviceCallLogsData = useMemo(
    () => deviceCallLogsQuery?.data?.data ?? [],
    [deviceCallLogsQuery?.data?.data],
  );

  const searchBarPlaceholder = t('common:search.placeholder');

  const dateFilter = useMemo(
    () => ({
      startDate,
      endDate,
      getDateRangeStatus,
      setDateRange,
      isError: isDateRangeError,
      setPeriod,
      timePeriod,
      isStartDateError,
      isEndDateError,
    }),
    [
      endDate,
      getDateRangeStatus,
      isDateRangeError,
      isEndDateError,
      isStartDateError,
      setDateRange,
      setPeriod,
      startDate,
      timePeriod,
    ],
  );

  const errorMessage = useMemo(
    () => getLogicalDeviceHistoryTabErrorMsg(t, deviceCallLogsQuery?.error),
    [t, deviceCallLogsQuery?.error],
  );
  const errorStatus = useMemo(() => deviceCallLogsQuery?.error?.status, [deviceCallLogsQuery?.error]);
  const errorTitle = useMemo(() => t('title.noCallsFound', { ns: LNGS_NAMESPACE_LOGICAL_DEVICE.DEFAULT }), [t]);

  const noRowsProps = useMemo<EmptyListProps>(
    () => ({
      image: 'empty-services',
      messageLabel: 'emptyDeviceCallLogs.message',
      namespace: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
      titleLabel: '',
      isBordered: false,
    }),
    [],
  );

  const userDateTimeFormat = useCurrentUserDateTimeFormat();

  const handleRefresh = useCallback(() => {
    deviceCallLogsQuery.refetch();
  }, [deviceCallLogsQuery]);

  return {
    selectedQuickFilterValue,
    checkAllowReset,
    overrideDefaultFilterTypes,
    quickFilterCall,
    handleFilterChange,
    filterRefApi,
    searchBarPlaceholder,
    isLoadingQuickFilters,
    fields,
    setDebouncedSearchQuery,
    getLabelQuickFilterLabel,
    columns,
    filteredDeviceCallLogsData,
    gridProps,
    deviceCallLogsQuery,
    dateFilter,
    isFirstLoading,
    errorMessage,
    total,
    noRowsProps,
    userDateTimeFormat,
    userLocale,
    errorStatus,
    errorTitle,
    handleRefresh,
    ...datagridTriggers,
  };
}
